import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "../../../../components/Commons/Spinner/Spinner";
import { ReceiptDetail } from "../../../../interfaces/Sales/Receipts/receipts";
import { Box, Button } from "@mui/material";
import { useParamsFilter } from "../../../../hooks/useParamsFilter";
import { GridColDef, GridRenderCellParams, GridTreeNodeWithRender, GridValueGetterParams } from "@mui/x-data-grid";
import { useFormatNumber } from "../../../../hooks/useFormatNumber";
import { useDates } from "../../../../hooks/useDates";
import DataTable from "../../../../components/Tables/GridTableMaterialUI/DataTable";

type TabRelatedDocumentsProps = {
  receiptsDetailsData: ReceiptDetail[],
}

export const TabRelatedDocuments = ({
  receiptsDetailsData,
}: TabRelatedDocumentsProps) => {
  const [t] = useTranslation("global");
  const { TimeConverter } = useDates();
  const { ConvertParamsToBase64 } = useParamsFilter();
  const { setFormatNumberFromTable } = useFormatNumber();
  const [dataLoaded, setDataLoaded] = useState(false);

  const [myPreferences, setMyPreferences] = useState({});
  const [columns, setColumns] = useState<GridColDef<ReceiptDetail>[]>([
    {
      field: 'sales_invoice_name', headerName: t("salesinvoices.fields.sales_invoice_name"), headerClassName: 'header-grid-table', flex: 1,
      renderCell(params) {
        return <Button onClick={() => {
          let stringConverted = ConvertParamsToBase64(`sales_invoice_id=${params.row.sales_invoice_id}&customer_id=${params.row.customer_id}`);
          window.open(`/salesinvoices/view?${stringConverted}`, '_blank');
        }}>
          {params.row.sales_invoice_name}
        </Button>
      }
    },
    { field: 'customer_business_name', headerName: t("receiptsdetails.fields.customer_bussines_name"), headerClassName: 'header-grid-table', flex: 1 },
    { field: 'receipt_name', headerName: t("receipts.fields.receipt_name"), headerClassName: 'header-grid-table', flex: 1 },
    { field: 'uuid', headerName: t("receipts.fields.uuid"), headerClassName: 'header-grid-table', flex: 1 },
    { field: 'status_name', headerName: t("receiptsdetails.fields.status_name"), headerClassName: 'header-grid-table', flex: 1 },
    {
      field: 'previous_amount', headerName: t("receiptsdetails.fields.previous_amount"), headerClassName: 'header-grid-table', flex: 1,
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
    {
      field: 'payment_amount', headerName: t("receiptsdetails.fields.payment_amount"), headerClassName: 'header-grid-table', flex: 1,
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
    {
      field: 'amount_outstanding', headerName: t("receiptsdetails.fields.amount_outstanding"), headerClassName: 'header-grid-table', flex: 1,
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
    {
      field: 'total_company_currency_amount', headerName: t("companies.configuration.fields.total_company_currency_amount"), headerClassName: 'header-grid-table', flex: 1,
      valueGetter(params) {
        return setFormatNumberFromTable(params.value ?? 0);
      },
    },
    { field: 'related_currency_code', headerName: t("currencies.title-view"), headerClassName: 'header-grid-table', flex: 1 },
    {
      field: 'payment_deadline', headerName: t("receiptsdetails.fields.payment_deadline"), headerClassName: 'header-grid-table', type: "date", flex: 1,
      valueGetter(params) {
        return TimeConverter(params.value ?? null);
      },
    },
    {
      field: 'creation_date', headerName: t("generic.creation_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
      valueGetter(params) {
        return TimeConverter(params.value);
      },
    },
    { field: 'last_update_user', headerName: t("generic.last_update_user"), headerClassName: 'header-grid-table', flex: 1 },
    {
      field: 'last_update_date', headerName: t("generic.last_update_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
      valueGetter(params) {
        return TimeConverter(params.value);
      },
    },
  ]);

  const loadPreferences = async () => {
    let myPreferences = await localStorage.getItem("grid-relatedreceiptsdetails");
    if (myPreferences != null) {
      setMyPreferences(JSON.parse(myPreferences));
    }
  }

  const loadColumnsOrder = async () => {
    let myOrderColumns = await localStorage.getItem("grid-relatedreceiptsdetails-columns");
    if (myOrderColumns != null) {
      let myJson = JSON.parse(myOrderColumns);
      for (let index = 0; index < myJson.length; index++) {
        const element = myJson[index];
        if (element['type'] === 'date') {
          element.headerName = t(`generic.${element.field}`);
          element.valueGetter = function (params: GridValueGetterParams<ReceiptDetail, any, GridTreeNodeWithRender>) {
            return TimeConverter(params.value);
          };
          if (element.field === 'payment_deadline') {
            element.headerName = t(`receiptsdetails.fields.${element.field}`);
          }
        }
        else {
          if (element.field === 'amount_outstanding' || element.field === 'total_company_currency_amount') {
            element.valueGetter = function (params: GridValueGetterParams<ReceiptDetail, any, GridTreeNodeWithRender>) {
              return setFormatNumberFromTable(params.value ?? 0);
            }
          }
          else if (element.field === 'last_update_user') { element.headerName = t(`generic.${element.field}`); }
          else if (element.field === 'sales_invoice_name') {
            element.headerName = t(`salesinvoices.fields.sales_invoice_name`);
          }
          else if (element.field === 'uuid') {
            element.headerName = t(`receipts.fields.uuid`);
            element.renderCell = function (params: GridRenderCellParams<ReceiptDetail, any, any, GridTreeNodeWithRender>) {
              return <Button onClick={() => {
                let stringConverted = ConvertParamsToBase64(`sales_invoice_id=${params.row.sales_invoice_id}&customer_id=${params.row.customer_id}`);
                window.open(`/salesinvoices/view?${stringConverted}`, '_blank');
              }}>
                {params.row.sales_invoice_name}
              </Button>
            }
          }
          else {
            element.headerName = t(`receiptsdetails.fields.${element.field}`);
          }
        }
      }
      setColumns(myJson);
    }
  }

  useEffect(() => {
    loadPreferences();
    loadColumnsOrder();

    setDataLoaded(true);
  }, []);

  return (
    <>
      {!dataLoaded && <Spinner isBox={false} />}
      {dataLoaded &&
        <Box className='screen-container'>
          <div className="d-flex justify-content-center">
            <DataTable
              columns={columns}
              setColumns={setColumns}
              data={receiptsDetailsData}
              entityId={"receipt_detail_id"}
              entity={`ReceiptsDetails`}
              preferences={myPreferences}
              namePreferences={"grid-receiptsdetails"}
              nameOrderColumns={"grid-receiptsdetails-columns"}
              isChildren={true}
              countColumns={["total_company_currency_amount"]}
            />
          </div>
        </Box>
      }
    </>
  )
}
